import { useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { useParams } from "react-router";

import * as api from "../../api/api";
import { useDroneMissionsRtdbDocuments } from "../../api/rtdbWrappers.generated";
import { useDronesOnlineState } from "../../hooks/useDroneOnline.jsx";

export default function DroneSelector({
    value,
    onChange,
    allowUnavailable = false,
    onSelectedDroneUnavailableReasonChange,
}) {
    const { organizationId, locationId } = useParams();
    const [droneIds] = api.useDroneIdsFromLocation(organizationId, locationId);
    const [droneMissions, droneMissionsAreLoading] =
        useDroneMissionsRtdbDocuments(droneIds, {
            type: "map",
        });
    const { onlineStatuses, isLoading: onlineStatusesAreLoading } =
        useDronesOnlineState(droneIds);
    const selectedDroneUnavailableReason = useMemo(() => {
        if (
            !allowUnavailable ||
            !value ||
            value === "none" ||
            value === "ANY"
        ) {
            return null;
        }

        if (droneMissionsAreLoading || onlineStatusesAreLoading) {
            return "Checking whether the selected drone is available.";
        }

        if ((droneMissions?.[value] || []).length > 0) {
            return "The selected drone has an active mission, so this task cannot be started.";
        }

        if (onlineStatuses[value] !== true) {
            return "The selected drone is offline, so this task cannot be started.";
        }

        return null;
    }, [
        droneMissions,
        droneMissionsAreLoading,
        onlineStatuses,
        onlineStatusesAreLoading,
        allowUnavailable,
        value,
    ]);

    useEffect(() => {
        onSelectedDroneUnavailableReasonChange?.(
            selectedDroneUnavailableReason,
        );
    }, [
        onSelectedDroneUnavailableReasonChange,
        selectedDroneUnavailableReason,
    ]);

    return (
        <Form.Select
            value={value}
            onChange={(event) => {
                onChange(event.target.value);
            }}
        >
            <option disabled value="none">
                Select a drone ...
            </option>
            <option key="ANY" value="ANY">
                Any drone
            </option>
            {droneIds.map((droneId) => {
                const hasActiveMission =
                    (droneMissions?.[droneId] || []).length > 0;
                const isOffline =
                    !onlineStatusesAreLoading &&
                    onlineStatuses[droneId] !== true;
                let unavailableLabel = "";
                if (hasActiveMission) {
                    unavailableLabel = "has active mission";
                } else if (allowUnavailable && isOffline) {
                    unavailableLabel = "offline";
                }

                return (
                    <option
                        disabled={hasActiveMission && !allowUnavailable}
                        key={droneId}
                        value={droneId}
                    >
                        {`${droneId}${unavailableLabel ? ` (${unavailableLabel})` : ""}`}
                    </option>
                );
            })}
        </Form.Select>
    );
}
